import { useState } from "react";
import { Send, Mail, MessageCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { CONTACT } from "@/constants/testIds";
import { submitContact } from "@/lib/api";
import { useContent } from "@/context/ContentContext";

const initial = { name: "", email: "", subject: "Contact via site vitrine", message: "" };

export const Contact = () => {
  const { content } = useContent();
  const contactEmail = content.company.email || content.founder.email || "i.wilson@whilsco.fr";
  const whatsappRaw = content.company.whatsapp || "";
  const whatsappDigits = whatsappRaw.replace(/[^0-9]/g, "");
  const whatsappUrl = whatsappDigits ? `https://wa.me/${whatsappDigits}` : "#";

  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    if (!form.name || form.name.trim().length < 2) {
      toast.error("Veuillez saisir votre nom.");
      return false;
    }
    if (!form.email || !/^\S+@\S+\.\S+$/.test(form.email)) {
      toast.error("Veuillez saisir une adresse email valide.");
      return false;
    }
    if (!form.message || form.message.trim().length < 5) {
      toast.error("Votre message est trop court.");
      return false;
    }
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!validate()) return;

    setLoading(true);
    try {
      const res = await submitContact({
        name: form.name,
        email: form.email,
        subject: form.subject || "Contact site Zafara",
        message: form.message,
      });
      setSent(true);
      setForm(initial);
      toast.success(res.message || "Message envoyé.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erreur lors de l'envoi. Réessayez.";
      toast.error(typeof msg === "string" ? msg : "Erreur lors de l'envoi.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid={CONTACT.root}
      className="relative py-24 md:py-32 bg-[#FBFAF6] border-y border-[#EDE7D9]"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="font-utility text-xs uppercase tracking-[0.25em] text-[#0E7A3B]">Contact</p>
            <h2 className="mt-4 font-display text-4xl md:text-6xl text-[#16241B] leading-[1.02] tracking-tighter">
              Une question ?
              <br />
              <span className="text-[#0E7A3B]">Écrivez-nous.</span>
            </h2>
            <p className="mt-6 text-lg text-[#16241B]/75 leading-relaxed max-w-md">
              Presse, partenariat, retour utilisateur ou simple curiosité —
              l'équipe Whilsco-Digit vous répond sous 24h ouvrées.
            </p>

            <div className="mt-10 space-y-4">
              <a href={`mailto:${contactEmail}`} className="flex items-start gap-4 group">
                <div className="mt-0.5 flex h-11 w-11 items-center justify-center rounded-xl bg-white border border-[#EDE7D9] group-hover:border-[#0E7A3B] transition-colors">
                  <Mail className="h-5 w-5 text-[#0E7A3B]" />
                </div>
                <div>
                  <p className="font-utility text-[10px] uppercase tracking-widest text-[#16241B]/50">Email</p>
                  <p className="mt-0.5 font-display text-lg text-[#16241B] group-hover:text-[#0E7A3B] transition-colors tracking-tight break-all">
                    {contactEmail}
                  </p>
                </div>
              </a>

              {whatsappDigits && (
                <a
                  data-testid={CONTACT.whatsapp}
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 group"
                >
                  <div className="mt-0.5 flex h-11 w-11 items-center justify-center rounded-xl bg-white border border-[#EDE7D9] group-hover:border-[#0E7A3B] transition-colors">
                    <MessageCircle className="h-5 w-5 text-[#0E7A3B]" />
                  </div>
                  <div>
                    <p className="font-utility text-[10px] uppercase tracking-widest text-[#16241B]/50">WhatsApp</p>
                    <p className="mt-0.5 font-display text-lg text-[#16241B] group-hover:text-[#0E7A3B] transition-colors tracking-tight">
                      {whatsappRaw}
                    </p>
                  </div>
                </a>
              )}
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              data-testid={CONTACT.form}
              onSubmit={submit}
              noValidate
              className="rounded-3xl border border-[#EDE7D9] bg-white p-8 md:p-10 shadow-[0_20px_60px_rgba(14,122,59,0.06)]"
            >
              {sent && (
                <div className="mb-6 flex items-center gap-3 rounded-2xl border border-[#0E7A3B]/20 bg-[#0E7A3B]/5 px-4 py-3 text-sm text-[#0E7A3B]">
                  <CheckCircle2 className="h-5 w-5" />
                  Merci ! Votre message est bien parti. On revient vite vers vous.
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <label className="block">
                  <span className="font-utility text-[10px] uppercase tracking-widest text-[#16241B]/60">Nom complet</span>
                  <input
                    data-testid={CONTACT.name}
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Ex : Amaka H."
                    className="mt-2 w-full rounded-xl border border-[#EDE7D9] bg-[#FBFAF6] px-4 py-3 text-base text-[#16241B] placeholder:text-[#16241B]/30 focus:outline-none focus:ring-2 focus:ring-[#0E7A3B] focus:border-[#0E7A3B] transition-colors"
                  />
                </label>
                <label className="block">
                  <span className="font-utility text-[10px] uppercase tracking-widest text-[#16241B]/60">Email</span>
                  <input
                    data-testid={CONTACT.email}
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="vous@email.com"
                    className="mt-2 w-full rounded-xl border border-[#EDE7D9] bg-[#FBFAF6] px-4 py-3 text-base text-[#16241B] placeholder:text-[#16241B]/30 focus:outline-none focus:ring-2 focus:ring-[#0E7A3B] focus:border-[#0E7A3B] transition-colors"
                  />
                </label>
              </div>

              <label className="block mt-5">
                <span className="font-utility text-[10px] uppercase tracking-widest text-[#16241B]/60">Votre message</span>
                <textarea
                  data-testid={CONTACT.message}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Dites-nous ce qui vous amène…"
                  rows={6}
                  className="mt-2 w-full rounded-xl border border-[#EDE7D9] bg-[#FBFAF6] px-4 py-3 text-base text-[#16241B] placeholder:text-[#16241B]/30 focus:outline-none focus:ring-2 focus:ring-[#0E7A3B] focus:border-[#0E7A3B] transition-colors resize-y"
                />
              </label>

              <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
                <p className="text-xs text-[#16241B]/50 max-w-xs">
                  En envoyant ce formulaire, vous acceptez d'être recontacté par
                  l'équipe Whilsco-Digit.
                </p>
                <button
                  data-testid={CONTACT.submit}
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center gap-2 rounded-full bg-[#0E7A3B] px-7 py-3.5 text-sm font-semibold text-white shadow-[0_10px_30px_rgba(14,122,59,0.35)] hover:bg-[#F5A800] hover:text-[#16241B] hover:-translate-y-0.5 hover:shadow-[0_14px_38px_rgba(245,168,0,0.45)] disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300"
                >
                  <Send className="h-4 w-4" />
                  {loading ? "Envoi en cours…" : "Envoyer le message"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
